import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { t } from '@lingui/macro';

import { Button, Combobox, Flex, TextField } from '@decisiv/ui-components';
import MinusCircle from '@decisiv/iconix/lib/components/MinusCircle';
import PlusCircle from '@decisiv/iconix/lib/components/PlusCircle';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import filterOptions from '../../utils/filterOptions';
import WmiCodesApi from '../../api/wmi_codes';
import AttributePicker, { sourceShape } from './AttributePicker';

const MultilineHelpWrapper = styled.div`
  white-space: pre-line;
`;

export default function EditExecutionConditions({
  executionConditions,
  handlers,
  editDisabled = true,
  errorsArray = {},
  payloadSources = [],
}) {
  const shouldShowCharCount = (errorAttribute) => isEmpty(errorAttribute);
  const ATTRIBUTE_HELP = t`Dot-path to the attribute in the payload.
Application events: nested, eg: data.asset.vin
HTTP: usually root-level, eg: vin`;
  const [inputValue, setInputValue] = useState('');
  const [wmiOptions, setWmiOptions] = useState([]);
  const [wmiInputValue, setWmiInputValue] = useState('');

  useEffect(() => {
    const loadWmiCodes = async () => {
      const result = await WmiCodesApi.fetchAllWmiCodes();
      if (result.data) {
        const groupOptions = result.data.map((item) => ({
          id: item.attributes.oem_name,
          label: item.attributes.oem_name,
          value: item.attributes.oem_name,
        }));

        // Tags live in their own namespace: the stored value is prefixed with
        // `tag:` so the backend routes it to the union of every group carrying the
        // tag, and a tag may share a name with a group without colliding.
        const tagNames = new Set();
        result.data.forEach((item) => {
          (item.attributes.tags || []).forEach((tag) => {
            const trimmed = typeof tag === 'string' ? tag.trim() : '';
            if (trimmed) tagNames.add(trimmed);
          });
        });
        const tagOptions = [...tagNames].map((tag) => ({
          id: `tag:${tag}`,
          label: `${tag} ${t`(Tag)`}`,
          value: `tag:${tag}`,
        }));

        setWmiOptions([...groupOptions, ...tagOptions]);
      }
    };
    loadWmiCodes();
  }, []);

  const rules = [
    { id: 'is_present', label: 'Is present', value: 'is_present' },
    { id: 'is_not_present', label: 'Is not present', value: 'is_not_present' },
    { id: 'is_true', label: 'Is true', value: 'is_true' },
    { id: 'is_false', label: 'Is false', value: 'is_false' },
    { id: 'equals', label: 'Equals to', value: 'equals' },
    { id: 'not_equals', label: 'Not equals to', value: 'not_equals' },
    { id: 'contains', label: 'Contains', value: 'contains' },
    { id: 'not_contains', label: 'Does not contains', value: 'not_contains' },
    { id: 'greater_than', label: 'Is greater than', value: 'greater_than' },
    { id: 'less_than', label: 'Is less than', value: 'less_than' },
    {
      id: 'greater_than_or_equal_to',
      label: 'Is greater than or equal to',
      value: 'greater_than_or_equal_to',
    },
    {
      id: 'less_than_or_equal_to',
      label: 'Is less than or equal to',
      value: 'less_than_or_equal_to',
    },
    { id: 'match', label: 'Matches', value: 'match' },
    {
      id: 'wmi_validation',
      label: 'WMI Validation',
      value: 'wmi_validation',
    },
  ];

  // Mirrors the event shape handlers.changeText reads, as the WMI combobox does.
  const applyAttribute = (index, path) => {
    const name = `condition-attribute-${index}`;
    handlers.changeText({
      e: { target: { name, value: path }, currentTarget: { name } },
      index,
    });
  };

  const AddNewCondition = () => {
    handlers.addEntry();
    setInputValue('');
  };

  return (
    <>
      {executionConditions[0].rules.map((item, index) => (
        <Flex
          key={item.rowId}
          justifyContent="flex-start"
          alignItems="flex-start"
          style={{
            whiteSpace: 'nowrap',
            gap: '1.25rem',
            margin: '-1px',
            border: '1px solid var(--if-border)',
            padding: '0.625rem',
          }}
        >
          <Button
            aria-label={t`Remove entry`}
            variant="ghost"
            icon={MinusCircle}
            disabled={editDisabled}
            onClick={() => handlers.removeEntry(index)}
          />
          <MultilineHelpWrapper>
            {payloadSources.length > 0 ? (
              <AttributePicker
                required
                value={item.condition_attribute}
                label={t`attribute`}
                name={`condition-attribute-${index}`}
                style={{ marginTop: '0.5rem' }}
                helpMessage={ATTRIBUTE_HELP}
                disabled={editDisabled}
                showCharacterCount={shouldShowCharCount(
                  errorsArray[`condition-attribute-${index}`],
                )}
                warningMessage={errorsArray[`condition-attribute-${index}`]}
                onChange={(e) => handlers.changeText({ e, index })}
                sources={payloadSources}
                onSelect={(path) => applyAttribute(index, path)}
              />
            ) : (
              <TextField
                required
                value={item.condition_attribute}
                label={t`attribute`}
                name={`condition-attribute-${index}`}
                style={{ marginTop: '0.5rem' }}
                helpMessage={ATTRIBUTE_HELP}
                disabled={editDisabled}
                showCharacterCount={shouldShowCharCount(
                  errorsArray[`condition-attribute-${index}`],
                )}
                warningMessage={errorsArray[`condition-attribute-${index}`]}
                onChange={(e) => handlers.changeText({ e, index })}
              />
            )}
          </MultilineHelpWrapper>
          <Flex style={{ width: '18.75rem' }}>
            <Combobox
              required
              label={t`rule`}
              helpMessage={t`eg: attribute equals value`}
              style={{ marginTop: '0.5rem' }}
              disabled={editDisabled}
              options={filterOptions(rules, inputValue)}
              onChangeInputValue={(e) => setInputValue(e ? e.target.value : '')}
              onChangeValue={(e) => handlers.changeOption({ e, index })}
              defaultInputValue={
                rules.find((rule) => rule.value === item.rule)?.label
              }
            />
          </Flex>
          {item.rule === 'wmi_validation' ? (
            <Flex style={{ width: '18.75rem' }}>
              <Combobox
                required
                label={t`value`}
                helpMessage={t`Select a WMI group or tag`}
                style={{ marginTop: '0.5rem' }}
                disabled={editDisabled}
                options={filterOptions(wmiOptions, wmiInputValue)}
                onChangeInputValue={(e) =>
                  setWmiInputValue(e ? e.target.value : '')
                }
                onChangeValue={(selectedValue) => {
                  const syntheticEvent = {
                    target: {
                      name: `condition-value-${index}`,
                      value: selectedValue || '',
                    },
                    currentTarget: {
                      name: `condition-value-${index}`,
                    },
                  };
                  handlers.changeText({
                    e: syntheticEvent,
                    item,
                    index,
                  });
                }}
                defaultInputValue={
                  wmiOptions.find((opt) => opt.value === item.condition_value)
                    ?.label || item.condition_value
                }
              />
            </Flex>
          ) : (
            <TextField
              required
              value={item.condition_value}
              label={t`value`}
              name={`condition-value-${index}`}
              style={{ marginTop: '0.5rem' }}
              helpMessage={t`eg: value to be tested to`}
              disabled={
                editDisabled ||
                [
                  'is_present',
                  'is_not_present',
                  'is_true',
                  'is_false',
                ].includes(item.rule)
              }
              showCharacterCount={shouldShowCharCount(
                errorsArray[`condition-value-${index}`],
              )}
              warningMessage={errorsArray[`condition-value-${index}`]}
              onChange={(e) => handlers.changeText({ e, item, index })}
            />
          )}
        </Flex>
      ))}
      <Flex
        justifyContent="flex-start"
        alignItems="center"
        style={{
          border: '1px solid var(--if-border)',
          margin: '-1px',
          gap: '1.25rem',
          padding: '0.625rem',
        }}
      >
        <Button
          aria-label={t`Add Execution Condition`}
          text={t`Add Execution Condition`}
          icon={PlusCircle}
          variant="ghost"
          disabled={editDisabled}
          onClick={AddNewCondition}
        />
      </Flex>
    </>
  );
}

EditExecutionConditions.propTypes = {
  executionConditions: PropTypes.arrayOf(
    PropTypes.shape({
      condition_type: PropTypes.string.isRequired,
      rules: PropTypes.arrayOf(
        PropTypes.shape({
          rowId: PropTypes.string,
          condition_attribute: PropTypes.string,
          rule: PropTypes.string,
          condition_value: PropTypes.string,
        }),
      ).isRequired,
    }),
  ).isRequired,
  handlers: PropTypes.shape({
    addEntry: PropTypes.func.isRequired,
    removeEntry: PropTypes.func.isRequired,
    changeText: PropTypes.func.isRequired,
    changeOption: PropTypes.func.isRequired,
  }).isRequired,
  editDisabled: PropTypes.bool,
  errorsArray: PropTypes.objectOf(PropTypes.string),
  payloadSources: PropTypes.arrayOf(sourceShape),
};
